import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

export interface Association {
  id: string;
  name: string;
  description: string | null;
  logo_url: string | null;
  logo_square_url: string | null;
  primary_color: string | null;
  website_url: string | null;
  contact_email: string | null;
  created_at: string;
  updated_at: string;
}

export interface AssociationMember {
  id: string;
  association_id: string;
  user_id: string;
  role: string;
  role_type?: string;
  display_name: string | null;
  status?: string;
  created_at: string;
}

export interface AssociationOrgStats {
  org_id: string;
  org_name: string;
  logo_url: string | null;
  logo_square_url: string | null;
  primary_color: string | null;
  created_at: string;
  active_members: number;
  total_members: number;
  recent_events: number;
  ytd_revenue: number;
}

export interface AssociationDashboard {
  orgs: AssociationOrgStats[];
  total_orgs: number;
  total_active_members: number;
  total_members: number;
  total_recent_events: number;
}

/** Fetch all associations the current user belongs to */
export const useUserAssociations = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["user_associations", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.from("associations").select("*").order("name");
      if (error) throw error;
      return (data || []) as unknown as Association[];
    },
    enabled: !!user,
  });
};

/** Fetch single association */
export const useAssociation = (associationId: string | null) =>
  useQuery({
    queryKey: ["association", associationId],
    queryFn: async () => {
      const { data, error } = await supabase.from("associations").select("*").eq("id", associationId!).maybeSingle();
      if (error) throw error;
      return data as unknown as Association;
    },
    enabled: !!associationId,
  });

/** Fetch association dashboard data */
export const useAssociationDashboard = (associationId: string | null) =>
  useQuery({
    queryKey: ["association_dashboard", associationId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_association_dashboard", {
        _association_id: associationId,
      });
      if (error) throw error;
      return data as unknown as AssociationDashboard;
    },
    enabled: !!associationId,
    staleTime: 2 * 60 * 1000,
  });

/** Fetch association members */
export const useAssociationMembers = (associationId: string | null) =>
  useQuery({
    queryKey: ["association_members", associationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("association_users")
        .select("*")
        .eq("association_id", associationId!)
        .order("created_at");
      if (error) throw error;
      return (data || []).map((d: Tables<"association_users">) => ({
        id: d.id,
        association_id: d.association_id,
        user_id: d.user_id,
        role: d.role_type,
        role_type: d.role_type,
        display_name: d.display_name,
        status: d.status,
        created_at: d.created_at ?? "",
      })) as AssociationMember[];
    },
    enabled: !!associationId,
  });

/** Create association */
export const useCreateAssociation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; name: string; description?: string; contact_email?: string }) => {
      const { data, error } = await supabase.from("associations").insert(input).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["user_associations"] });
      qc.invalidateQueries({ queryKey: ["master_all_associations"] });
      toast.success("Association created");
    },
    onError: (e: Error) => toast.error(e.message),
  });
};

/** Update association */
export const useUpdateAssociation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Association> & { id: string }) => {
      const { error } = await supabase.from("associations").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["association", vars.id] });
      qc.invalidateQueries({ queryKey: ["user_associations"] });
      toast.success("Association updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
};

/** Delete association */
export const useDeleteAssociation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("associations").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["user_associations"] });
      qc.invalidateQueries({ queryKey: ["master_all_associations"] });
      toast.success("Association deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });
};

/** Add org to association */
export const useLinkOrgToAssociation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orgId, associationId }: { orgId: string; associationId: string }) => {
      const { error } = await supabase.from("organizations").update({ association_id: associationId }).eq("id", orgId);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["association_dashboard", vars.associationId] });
      toast.success("Organization linked to association");
    },
    onError: (e: Error) => toast.error(e.message),
  });
};

/** Remove org from association */
export const useUnlinkOrgFromAssociation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orgId, associationId }: { orgId: string; associationId: string }) => {
      const { error } = await supabase.from("organizations").update({ association_id: null }).eq("id", orgId);
      if (error) throw error;
      return associationId;
    },
    onSuccess: (assocId) => {
      qc.invalidateQueries({ queryKey: ["association_dashboard", assocId] });
      toast.success("Organization removed from association");
    },
    onError: (e: Error) => toast.error(e.message),
  });
};

/** Add member to association */
export const useAddAssociationMember = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { association_id: string; user_id: string; role?: string; display_name?: string }) => {
      const auPayload = {
        association_id: input.association_id,
        user_id: input.user_id,
        role_type: input.role === "admin" ? "association_admin" : "association_staff",
        display_name: input.display_name || null,
        status: "active",
        joined_at: new Date().toISOString(),
      };
      const { data, error } = await supabase
        .from("association_users")
        .upsert(auPayload, { onConflict: "association_id,user_id" })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["association_members", vars.association_id] });
      toast.success("Member added to association");
    },
    onError: (e: Error) => toast.error(e.message),
  });
};

/** Fetch shared settings for an association */
export const useAssociationSharedSettings = (associationId: string | null) =>
  useQuery({
    queryKey: ["association_shared_settings", associationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("association_shared_settings")
        .select("*")
        .eq("association_id", associationId!)
        .order("setting_type");
      if (error) throw error;
      return data || [];
    },
    enabled: !!associationId,
  });

/** Check if current user is an association admin */
export const useIsAssociationAdmin = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["is_association_admin", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("association_users")
        .select("association_id, role_type")
        .eq("user_id", user!.id)
        .eq("role_type", "association_admin")
        .eq("status", "active");
      if (error) return { isAssociationAdmin: false, associationIds: [] as string[] };
      const ids = (data || []).map((d) => d.association_id);
      return { isAssociationAdmin: ids.length > 0, associationIds: ids };
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
};
